// Background blur (F-CALL-5): a LiveKit track processor applied to the local
// camera track PRE-PUBLISH — i.e. before E2EE encryption, on the raw frame,
// so encryption is unaffected (§5). MediaPipe Selfie Segmentation via
// @livekit/track-processors, on the GPU/modern path.
//
// §4.2 (NON-NEGOTIABLE, zero external runtime deps): the MediaPipe WASM
// fileset and the segmentation model are SELF-HOSTED — assetPaths point at our
// own origin (default /mediapipe/…), never a CDN. The web app vendors them via
// scripts/fetch-mediapipe-assets (download-once, build-time).

import {
  BackgroundProcessor,
  type BackgroundProcessorWrapper,
  supportsBackgroundProcessors,
} from '@livekit/track-processors';
import type { LocalVideoTrack } from 'livekit-client';

export interface BlurAssetPaths {
  /** Directory holding the MediaPipe tasks-vision WASM fileset. */
  tasksVisionFileSet: string;
  /** The selfie-segmentation model file. */
  modelAssetPath: string;
}

export const DEFAULT_BLUR_ASSETS: BlurAssetPaths = {
  tasksVisionFileSet: '/mediapipe/wasm',
  modelAssetPath: '/mediapipe/selfie_segmenter.tflite',
};

// Frame-time budget: if segmentation regularly exceeds this, the hardware is
// too slow — auto-disable and warn (F-CALL-5).
const FRAME_BUDGET_MS = 40; // ~25fps segmentation ceiling
const OVERBUDGET_STREAK = 30; // consecutive slow frames before giving up

export function blurSupported(): boolean {
  return supportsBackgroundProcessors();
}

/** Owns the blur processor lifecycle for one local camera track. */
export class BlurController {
  private processor: BackgroundProcessorWrapper | null = null;
  private overBudget = 0;

  constructor(
    private assets: BlurAssetPaths,
    /** Called when the frame-time heuristic auto-disables blur. */
    private onAutoDisabled: () => void,
  ) {}

  get active(): boolean {
    return this.processor !== null;
  }

  async enable(track: LocalVideoTrack, blurRadius = 12): Promise<void> {
    if (this.processor) return;
    this.overBudget = 0;
    this.processor = BackgroundProcessor({
      mode: 'background-blur',
      blurRadius,
      assetPaths: {
        tasksVisionFileSet: this.assets.tasksVisionFileSet,
        modelAssetPath: this.assets.modelAssetPath,
      },
      onFrameProcessed: (stats) => this.watchFrame(track, stats.segmentationTimeMs),
    });
    await track.setProcessor(this.processor);
  }

  async disable(track: LocalVideoTrack): Promise<void> {
    if (!this.processor) return;
    this.processor = null;
    await track.stopProcessor();
  }

  private watchFrame(track: LocalVideoTrack, segMs: number): void {
    if (!this.processor) return;
    if (segMs > FRAME_BUDGET_MS) {
      this.overBudget += 1;
      if (this.overBudget >= OVERBUDGET_STREAK) {
        void this.disable(track);
        this.onAutoDisabled();
      }
    } else if (this.overBudget > 0) {
      this.overBudget -= 1; // recover slowly from transient spikes
    }
  }
}
